import constants from '@/constants'
import { stringToHTML, getLinkExtractionBody, restoreLinkToBody, validateUrl, replaceStringFromSpecifcIndex } from '@/utils/String'

// 特定のパーツにおいて、固定値で設定する余白
const followButton = {
  wrapperPadding: 'padding: 0 8px 10px 8px',
  textPadding: '5px'
}

const TABLE_DEFAULT_ATTRS = 'border="0" cellpadding="0" cellspacing="0"'
const MARGIN_CENTER = '0 auto'
const MARGIN_LEFT = '0 auto 0 0'
// outlook は margin でバグがあるため align を設定する
const MARGIN_2_ALIGN = {
  '0 auto 0 0': 'left',
  '0 auto': 'center',
  '0 0 0 auto': 'right',
}
// コードパーツ用の独自属性名
const { textAttrName, linkAttrName, imageAttrName, imageKeyAttrName, labelAttrName } = constants.part.code

// style=""に引数から渡したstyleを入れる
// プロパティに inner_ があれば削除：セクション内側の設定を識別するための接頭語なので、style作成時には不要
export const getStringStyle = styles => {
  return Object.keys(styles).map(prop => `${prop.replace('inner_', '')}: ${styles[prop]};`).join(' ')
}

// part.stylesを分別する
export const getFilteredStyles = (styles, styleKeys) => {
  const filteredStyles = {}
  styleKeys.forEach(styleKey => {
    if (!styles[styleKey]) return // 未定義なら追加しない
    if (styleKey === 'font-family-name') return // 表示に不要な項目のため追加しない
    filteredStyles[styleKey] = styles[styleKey]
  })
  return filteredStyles
}

export const generate = ({
  part,
  partsContainerType,
  layoutType,
  flgImageContentMobileFullWidth = 1,
  linkParams,
  isLinkChecker = false,
  sectionStyles,
  shareText = null,
}) => {
  let { type, body, styles, attrs } = part
  let partInner = null
  let followButtonPartHTML = null // フォローボタン専用：他のパーツと構造が異なるため
  // テーマのスタイル(outlook)
  let sectionStyle4Outlook = ''
  let textAlign2Align = ''
  if (sectionStyles) {
    sectionStyle4Outlook = getFilteredStyles(sectionStyles, ['color', 'font-size', 'line-height'])
    textAlign2Align = sectionStyles['text-align'] && sectionStyles['text-align'] !== 'justify' ? `align="${sectionStyles['text-align']}"` : ''
  }

  switch (type) {
    // テキスト
    case 'Text':
      body = embedLinkParams(body, linkParams)
      const partKeys = Object.keys(styles)
      const partStyles = getFilteredStyles(styles, partKeys)
      // パーツに text-align が設定されていたら align を上書きする
      if (styles['text-align'] && styles['text-align'] !== 'justify') {
        textAlign2Align = `align="${styles['text-align']}"`
      }
      partInner = `
        <td class="part-text" style="width: 100%; ${getStringStyle(sectionStyle4Outlook)} ${getStringStyle(partStyles)} word-break: break-word; padding-right: 5px;" ${textAlign2Align}>
          ${body}
        </td>`
      break
    // テキストボックス
    case 'BoxedText':
      body = embedLinkParams(body, linkParams)
      const boxedText = constants.part.boxedText
      // テキストボックス外側のスタイル：パーツコンテナが画像カード以外の場合、余白を設定
      const boxedTextContainerWrapperStyles = partsContainerType !== 'ImageCard' ? getFilteredStyles(styles, boxedText.containerWrapperStyleKeys) : ''
      // ボックスのスタイル
      const boxedTextContainerStyles = getFilteredStyles(styles, boxedText.containerStyleKeys)
      // テキストのスタイル
      const boxedTextInnerStyles = getFilteredStyles(styles, boxedText.innerStyleKeys)
      // テキストボックス内側の余白
      const boxedTextInnerPaddingStyles = styles['inner-padding'] ? { 'padding': styles['inner-padding'] } : { 'padding': 0 }
      // パーツに text-align が設定されていたら align を上書きする
      if (styles['text-align'] && styles['text-align'] !== 'justify') {
        textAlign2Align = `align="${styles['text-align']}"`
      }
      partInner = `
        <td style="width: 100%; ${getStringStyle(boxedTextContainerWrapperStyles)}">
          <table style="width: 100%; ${getStringStyle(boxedTextContainerStyles)} word-break: break-word;">
            <tbody>
              <tr>
                <td class="part-boxed-text" style="${getStringStyle(sectionStyle4Outlook)} ${getStringStyle(boxedTextInnerPaddingStyles)} ${getStringStyle(boxedTextInnerStyles)}" ${textAlign2Align}>
                  ${body}
                </td>
              </tr>
            </tbody>
          </table>
        </td>`
      break
    // 罫線
    case 'Divider':
      const divider = constants.part.divider
      // 外側のスタイル
      const dividerWrapperStyles = getFilteredStyles(styles, divider.wrapperStyleKeys)
      // 罫線のスタイル
      const dividerContainerStyles = getFilteredStyles(styles, divider.containerStyleKeys)
      partInner = `
        <td style="width: 100%; ${getStringStyle(dividerWrapperStyles)}">
          <table ${TABLE_DEFAULT_ATTRS} style="width: 100%; border-collapse: collapse; ${getStringStyle(dividerContainerStyles)}">
            <tbody>
              <tr>
                <td>
                  <span></span>
                </td>
              </tr>
            </tbody>
          </table>
        </td>
        <!--[if mso]><td></td><![endif]-->`
      break
    case 'Image':
      const imageHref = embedLinkParamsPartsAttrs(attrs.href, linkParams)
      const imageLinkCheckerClass = (isLinkChecker && attrs.linkCheckerClass) ? `class="${attrs.linkCheckerClass}"` : ''
      const imageTarget = attrs.target ? `target="${attrs.target}"` : ''
      const imageRel = attrs.target && attrs.target === '_blank' ? `rel="noopener"` : ''
      const imageTitle = attrs.title ? `title="${attrs.title}"` : ''
      const imageWrapperStyle = getFilteredStyles(styles, constants.part.image.wrapperStyleKeys)
      const imageTdStyle = getFilteredStyles(styles, constants.part.image.tdStyleKeys)
      const imageImgStyle = getFilteredStyles(styles, constants.part.image.imgStyleKeys)
      const imageCardImgWidthClass = partsContainerType === 'ImageCard' ? 'class="image-card-img-width"' : ''
      // padding未設定の場合、td要素にpadding: 0を適用する：メール表示時に、余白が入ることの防止
      // img要素にはdisplay:block; を入れる → これがないと、Gmailで余計な余白が入る
      const imageTdElementPadding = (!styles['padding-top'] || !styles['padding-bottom']) ? 'padding: 0;' : ''
      // モバイル表示用のクラスを付与
      let imageClass = null
      if (!attrs['data-key']) {
        // 画像未設定
        imageClass = 'mphe-image-content-mobile-full-width'
      } else if (partsContainerType === 'Image' || partsContainerType === 'Logo') {
        // 単体画像パーツ
        imageClass = 'mphe-image-content'
      } else if (partsContainerType === 'ImageCard') {
        // 画像カード
        imageClass = 'mphe-image-content-mobile-full-width'
      } else if (flgImageContentMobileFullWidth) {
        // 複数画像パーツで、画面幅フィットさせる
        imageClass = 'mphe-image-content-mobile-full-width'
      } else {
        // 複数画像パーツで、画面幅フィットさせない
        imageClass = 'mphe-image-content'
      }
      // outlook 画像サイズ爆発防止
      partInner = `
        <td class="part-image" style="width: 100%; ${imageTdElementPadding} ${getStringStyle(imageWrapperStyle)} ${getStringStyle(imageTdStyle)}">
          <!--[if mso]>
            <table ${TABLE_DEFAULT_ATTRS} width="100%"><tr><td align="${MARGIN_2_ALIGN[styles.margin]}">
              <span style="${getStringStyle(imageImgStyle)}">
                <a ${imageHref} ${imageTarget} ${imageRel} ${imageTitle} ${imageLinkCheckerClass} style="text-decoration: none;">
                  <img
                    width="${attrs.width.replace(/px/g, '')}"
                    height="${attrs.height.replace(/px/g, '')}"
                    src="${attrs.src}"
                    alt="${attrs.alt || ''}"
                    style="width: ${attrs.width}; height: auto;"
                    class="${imageClass}"
                  />
                </a>
              </span>
            </td></tr></table>
          <![endif]-->
          <!--[if !mso]><!-- -->
            <a ${imageHref} ${imageTarget} ${imageRel} ${imageTitle} ${imageLinkCheckerClass} style="text-decoration: none;">
              <img
                ${imageCardImgWidthClass}
                width="${attrs.width.replace(/px/g, '')}"
                height="${attrs.height.replace(/px/g, '')}"
                src="${attrs.src}"
                alt="${attrs.alt || ''}"
                class="${imageClass}"
                style="display: block; max-width: ${attrs.width}; height: auto; border-style: none; ${getStringStyle(imageImgStyle)}"
              />
            </a>
          <!--<![endif]-->
        </td>
      `

      break
    // ボタン
    case 'Button':
      const button = constants.part.button
      // ボタンの余白
      const buttonPaddingStyles = getFilteredStyles(styles, button.paddingStyleKeys)
      const buttonInnerPaddingStyles = styles['inner-padding'] ? { 'padding': styles['inner-padding'] } : { 'padding': 0 }
      // ボタン本体、内側、テキストのスタイル
      const buttonWrapperStyles = getFilteredStyles(styles, button.wrapperStyleKeys)
      const buttonContainerStyles = getFilteredStyles(styles, button.containerStyleKeys)
      const buttonInnerStyles = getFilteredStyles(styles, button.innerStyleKeys)
      // ボタン幅のスタイル
      const buttonDefaultStyles = {
        'border-collapse': 'separate',
        'width': attrs && attrs.width ? attrs.width : null
      }

      // ボタンのリンク、ターゲット、タイトル
      const buttonHref = attrs && attrs.href ? embedLinkParamsPartsAttrs(attrs.href, linkParams) : null
      const buttonLinkCheckerClass = (isLinkChecker && attrs && attrs.linkCheckerClass) ? `class="${attrs.linkCheckerClass}"` : ''
      const buttonTarget = attrs && attrs.target ? `target="${attrs.target}"` : ''
      const buttonRel = attrs && attrs.target && attrs.target === '_blank' ? `rel="noopener"` : ''
      const buttonTitle = attrs && attrs.title ? `title="${attrs.title}"` : ''
      partInner = `
        <td style="width: 100%; ${getStringStyle(buttonPaddingStyles)}">
          <!--[if mso]>
            <table ${TABLE_DEFAULT_ATTRS} style="width: 100%;"><tr><td align="${MARGIN_2_ALIGN[styles.margin]}">
          <![endif]-->
          <table style="${getStringStyle(buttonDefaultStyles)} ${getStringStyle(buttonWrapperStyles)}">
            <tbody>
              <tr>
                <td style="width: 100%; border-collapse: collapse; word-break: break-word; ${getStringStyle(buttonInnerPaddingStyles)} ${getStringStyle(buttonContainerStyles)}">
                  <a ${buttonHref} style="text-decoration: none;" ${buttonTarget} ${buttonRel} ${buttonTitle} ${buttonLinkCheckerClass}>
                    <span style="text-decoration: none; ${getStringStyle(buttonInnerStyles)}">
                      ${body}
                    </span>
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
          <!--[if mso]>
            </td></tr></table>
          <![endif]-->
        </td>`
      break
    // SNSアイコン
    case 'SnsIcon':
      const snsIconKeys = Object.keys(styles)
      const snsIconStyles = getFilteredStyles(styles, snsIconKeys)
      let snsIconHref = null
      if (partsContainerType === 'FollowButton') {
        snsIconHref = embedLinkParamsPartsAttrs(attrs.href, linkParams)
      } else {
        // シェアボタン
        snsIconHref = embedLinkParamsPartsAttrs(createSharePartHref(attrs['data-sns-name'], attrs.href, shareText), linkParams)
      }
      const snsIconLinkCheckerClass = (isLinkChecker && attrs.linkCheckerClass) ? `class="${attrs.linkCheckerClass}"` : ''
      const snsIconTarget = attrs.target ? `target="${attrs.target}"` : ''
      const snsIconRel = attrs.target && attrs.target === '_blank' ? `rel="noopener"` : ''
      const snsIconTitle = attrs.title ? `title="${attrs.title}"` : ''
      const snsIconLayoutType = attrs['data-display-sns']

      // パーツの外枠：パーツコンテナのレイアウトタイプによって、表示の調整
      let partDisplayStyle = ''
      let align = {
        snsIconTd: 'align="center"',
        partTable: 'align="center"',
      }
      if (layoutType.match(/horizontal/)) {
        // 横：インラインで横に並べる
        partDisplayStyle = { 'display': 'inline', 'margin': MARGIN_CENTER }
      } else if (layoutType === 'vertical') {
        // 縦：テキストのみの場合、中央寄せして縦位置を揃える / それ以外の場合、左寄せして縦位置を揃える
        if (snsIconLayoutType === 'Text') {
          partDisplayStyle = { 'margin': MARGIN_CENTER }
        } else {
          partDisplayStyle = { 'margin': MARGIN_LEFT }
          align = {
            snsIconTd: 'align="left"',
            partTable: '',
          }
        }
      } else if (layoutType === 'vertical2row') {
        // 縦2行：全てのパーツを中央寄せして、縦位置を揃える
        partDisplayStyle = { 'margin': MARGIN_CENTER }
      }
      // アイコン
      let displaySnsIcon = ''
      if (snsIconLayoutType.match(/Icon/)) {
        const snsIconWrapperStyle = { 'width': '100%', 'text-align': 'center' }
        // img要素にはdisplay:block; を入れる → これがないと、Gmailで余計な余白が入る
        // outlook 画像サイズ爆発防止
        displaySnsIcon = `
          <td class="part-sns-icon" style="${getStringStyle(snsIconWrapperStyle)}">
          <!--[if mso]>
            <table ${TABLE_DEFAULT_ATTRS} width="100%"><tr><td ${align.snsIconTd}>
              <a ${snsIconHref} ${snsIconTarget} ${snsIconRel} ${snsIconTitle} ${snsIconLinkCheckerClass} style="text-decoration: none;">
                <img
                  width="${attrs.width.replace(/px/g, '')}"
                  height="${attrs.height}"
                  src="${attrs.src}"
                  alt="${attrs.alt || ''}"
                  style="display: block; width: ${attrs.width}; margin: 0 auto;"
                />
              </a>
            </td></tr></table>
            <div style="display:none">
            <![endif]-->
            <a ${snsIconHref} ${snsIconTarget} ${snsIconRel} ${snsIconTitle} ${snsIconLinkCheckerClass} style="text-decoration: none;">
              <img width="${attrs.width}" height="${attrs.height}" src="${attrs.src}" alt="${attrs.alt || ''}" style="display: block; margin: 0 auto; max-width: ${attrs.width}; border-style: none;"/>
            </a>
            <!--[if mso]>
            </div>
            <![endif]-->
          </td>`
      }
      // テキスト
      const SnsLinkText = part.attrs && part.attrs.alt ? part.attrs.alt : ''
      let displaySnsText = ''
      if (snsIconLayoutType.match(/Text/)) {
        // 表示位置調整(共通)：垂直方向の位置を揃える
        const snsTextWrapperStyle = { 'width': '100%', 'vertical-align': 'middle' }
        // 表示位置調整(margin)：横並びレイアウトの場合のみ、左に寄せる
        if (layoutType.match(/horizontal/)) snsTextWrapperStyle['margin'] = MARGIN_LEFT
        // 表示位置調整(padding)：横並び2段組レイアウト以外の場合のみ、アイコンとくっつかないよう左に余白をとる
        const snsTextPadding = layoutType !== 'horizontal2row' ? { 'padding-left': followButton.textPadding } : ''
        displaySnsText = `
          <td style="${getStringStyle(snsTextWrapperStyle)} ${getStringStyle(snsTextPadding)}" align="center">
            <a ${snsIconHref} ${snsIconTarget} ${snsIconRel} ${snsIconTitle} ${snsIconLinkCheckerClass} style="text-decoration: none; white-space: nowrap;">
              <span style="text-decoration: none; ${getStringStyle(snsIconStyles)}">
                ${SnsLinkText}
              </span>
            </a>
          </td>`
      }
      // 1パーツで2行になるレイアウトの場合、テーブルの行を増やす
      const displayTableRow = layoutType.match(/2row/) ? '</tr><tr>' : ''

      followButtonPartHTML = `
        <table ${TABLE_DEFAULT_ATTRS} style="${getStringStyle(partDisplayStyle)}" ${align.partTable}>
          <tbody>
            <tr>
              <td style="${followButton.wrapperPadding};">
                <table ${TABLE_DEFAULT_ATTRS} style="width: 100%; margin: ${MARGIN_CENTER};">
                  <tbody>
                    <tr>
                      ${displaySnsIcon}
                      ${displayTableRow}
                      ${displaySnsText}
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          </tbody>
        </table>`
      break
    // フッター
    case 'Footer':
      body = embedLinkParams(body, linkParams)
      const footerKeys = Object.keys(styles)
      const footerStyles = getFilteredStyles(styles, footerKeys)
      // パーツに text-align が設定されていたら align を上書きする
      if (styles['text-align'] && styles['text-align'] !== 'justify') {
        textAlign2Align = `align="${styles['text-align']}"`
      }
      partInner = `
        <td class="part-footer" style="width: 100%; ${getStringStyle(sectionStyle4Outlook)} ${getStringStyle(footerStyles)} word-break: break-word; padding-right: 5px;" ${textAlign2Align}>
          ${body}
        </td>`
      break
    // スペーサー
    case 'Spacer':
      const spacerWidth = part.attrs['width'] ? part.attrs['width'] : '0px'
      const spacerHeight = part.attrs['height'] ? part.attrs['height'] : '0px'
      partInner = `
        <td class="part-spacer" style="width: 100%;">
          <table style="width: ${spacerWidth}; height: ${spacerHeight};">
            <tbody>
              <tr>
                <td> </td>
              </tr>
            </tbody>
          </table>
        </td>`
      break
    // コード
    case 'Code':
      let resultBody = ''
      let codeHrefs = []
      let codeSrcs = []
      body = embedLinkParams(body, linkParams)
      // リンクの&誤変換対策
      ;[body, codeHrefs, codeSrcs] = getLinkExtractionBody(body)
      const [HTMLObject, HTMLCollection] = stringToHTML(body)
      // 独自属性を取り除く
      if (HTMLObject && HTMLCollection) {
        for (let i = 0; i < HTMLCollection.length; i++) {
          const HTMLCollect = HTMLCollection[i]
          if (HTMLCollect.hasAttribute(textAttrName)) HTMLCollect.removeAttribute(textAttrName)
          if (HTMLCollect.hasAttribute(linkAttrName)) HTMLCollect.removeAttribute(linkAttrName)
          if (HTMLCollect.hasAttribute(imageAttrName)) HTMLCollect.removeAttribute(imageAttrName)
          if (HTMLCollect.hasAttribute(imageKeyAttrName)) HTMLCollect.removeAttribute(imageKeyAttrName)
          if (HTMLCollect.hasAttribute(labelAttrName)) HTMLCollect.removeAttribute(labelAttrName)
        }
        resultBody = HTMLObject.body.innerHTML
        // リンクの&誤変換対策
        resultBody = restoreLinkToBody(resultBody, codeHrefs, codeSrcs)
      }

      // テーマのスタイル(outlook)
      let codeSectionColor = ''
      let codeAlign2Align = ''
      if (sectionStyles) {
        codeSectionColor = sectionStyles['color'] ? `color: ${sectionStyles['color']};` : ''
        codeAlign2Align = sectionStyles['text-align'] && sectionStyles['text-align'] !== 'justify' ? `align="${sectionStyles['text-align']}"` : ''
      }
      partInner = `
        <td class="part-code" style="width: 100%; ${codeSectionColor} word-break: break-word;" ${codeAlign2Align}>
          ${resultBody}
        </td>`
      break
    default:
      partInner = part.body
  }

  if (type === 'SnsIcon') {
    return followButtonPartHTML
  } else {
    return `<table ${TABLE_DEFAULT_ATTRS} style="width: 100%; margin: auto 0; border-collapse: collapse;">
      <tbody>
        <tr>
          ${partInner}
        </tr>
      </tbody>
    </table>`
  }
}

// アナリティクス用リンクパラメータを文字内のURLに埋め込む
export const embedLinkParams = (text, linkParams) => {
  if (!linkParams) return text // リンクパラメータ未定義

  const hrefRegex = /<a[^>]*(href=["']?[^"']+["']?).*?>/g
  let hrefs = [...text.matchAll(hrefRegex)]
  if (hrefs.length < 1) return text // テキスト内にリンクが含まれていない
  let matchStartIndexDiff = 0 // 置換前と後で開始位置の差分が出るため、調整用
  hrefs.forEach(href => {
    const urlMatch = href[1].match(/href="([^"]*)"/)
    if (urlMatch && urlMatch[1]) {
      const embedLinkParamsHref = 'href="' + mergeQueryParameter(urlMatch[1], linkParams) + '"'
      text = replaceStringFromSpecifcIndex(
        text,
        href[1],
        embedLinkParamsHref,
        href.index - matchStartIndexDiff // 置換対象を探す開始位置
      )
      // 文字数の差分を追加
      matchStartIndexDiff += href[1].length - embedLinkParamsHref.length
    }
  })
  return text
}

// アナリティクス用リンクパラメータの埋め込み(part.attrs.href用)
export const embedLinkParamsPartsAttrs = (href, linkParams) => {
  if (!href) return ''
  if (href.startsWith('mailto:') || href.startsWith('tel:')) {
    // メールアドレスと電話番号にはアナリティクスパラメーターを埋め込まない
    return `href="${href}"`
  } else {
    const mergedHref = mergeQueryParameter(href, linkParams)
    return `href="${mergedHref}"`
  }
}

// SNSのURLかどうか
export const isSnsHref = (href) => {
  const url = new URL(href)
  let isSnsHref = false
  const snsUrls = [].concat(
    constants.xIcon.hrefs,
    constants.facebookIcon.hrefs,
    constants.youtubeIcon.hrefs,
    constants.instagramIcon.hrefs,
    constants.lineIcon.hrefs,
  )
  for (let i = 0; i < snsUrls.length; i++) {
    if (snsUrls[i] === url.origin) {
      isSnsHref = true
      break
    }
  }
  return isSnsHref
}

export const mergeQueryParameter = (href, linkParams) => {
  // リンクに既にクエリーパラメータが指定されているときは & でつなぐ
  // 不正な URL 形式、SNS アイコンは除外
  if (!linkParams || !validateUrl(href) || isSnsHref(href)) return href
  const url = new URL(href)
  return url.search ? href + '&' + linkParams : href + '?' + linkParams
}

export const createSharePartHref = (snsName, href, shareText) => {
  let sharePartHref = null
  switch (snsName) {
    case 'X':
      if (shareText && href) {
        sharePartHref = `https://x.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(href)}`
      } else if (shareText) {
        sharePartHref = `https://x.com/intent/tweet?text=${encodeURIComponent(shareText)}`
      } else if (href) {
        sharePartHref = `https://x.com/intent/tweet?url=${encodeURIComponent(href)}`
      }
      break
    case 'Facebook':
      if (href) {
        sharePartHref = `http://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(href)}`
      }
      break
    case 'LINE':
      if (shareText || href) {
        let lineShareText = null
        if (shareText && href) {
          lineShareText = `${shareText} ${href}`
        } else if (shareText) {
          lineShareText = shareText
        } else if (href) {
          lineShareText = href
        }
        sharePartHref = `https://line.me/R/share?text=${encodeURIComponent(lineShareText)}`
      }
      break
    default:
      sharePartHref = href
  }
  return sharePartHref
}
